import * as React from "react";
import {
  Box,
  Drawer,
  IconButton,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Button,
  Divider,
  CircularProgress,
} from "@mui/material";
import { createShareToken, listShareTokens, revokeShareToken } from "../../database/firebaseDatabase.js";
import { useAppTheme, PRESETS } from "../../theme/AppThemeContext.jsx";

// Icons
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import CloseIcon from "@mui/icons-material/Close";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import TuneIcon from "@mui/icons-material/Tune";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import PrivacyTipOutlinedIcon from "@mui/icons-material/PrivacyTipOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import CloudOutlinedIcon from "@mui/icons-material/CloudOutlined";
import AlternateEmailOutlinedIcon from "@mui/icons-material/AlternateEmailOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

const bg = "rgb(245, 245, 248)";
const muted = "rgb(133, 133, 133)";
const textDark = "rgb(20, 20, 20)";
const cardBg = "white";
const border = "1px solid rgba(0,0,0,0.06)";

export function SettingsIconWithDrawer({
  userEmail,
  userId,
  syncProviderLabel = "Structured Cloud",
  syncStatusLabel = "Fully synced",
  onResync,
  onOpenPrivacy,
  onLogout,
  onDeleteAccount,
  onResetApp,
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      {/* The SETTINGS ICON button */}
      <IconButton
        aria-label="Open settings"
        onClick={() => setOpen(true)}
        sx={{
          width: 34,
          height: 34,
          borderRadius: "50%",
          background: "white",
          border,
          "&:hover": { background: "white" },
        }}
      >
        <SettingsOutlinedIcon sx={{ color: muted }} />
      </IconButton>

      <SettingsDrawer
        open={open}
        onClose={() => setOpen(false)}
        userEmail={userEmail}
        userId={userId}
        syncProviderLabel={syncProviderLabel}
        syncStatusLabel={syncStatusLabel}
        onResync={onResync}
        onOpenPrivacy={onOpenPrivacy}
        onLogout={onLogout}
        onDeleteAccount={onDeleteAccount}
        onResetApp={onResetApp}
      />
    </>
  );
}

export function SettingsDrawer({
  open,
  onClose,
  userEmail,
  userId,
  syncProviderLabel = "Structured Cloud",
  syncStatusLabel = "Fully synced",
  onResync,
  onOpenPrivacy,
  onLogout,
  onDeleteAccount,
  onResetApp,
}) {
  const [active, setActive] = React.useState("account");

  React.useEffect(() => {
    if (open) setActive("account");
  }, [open]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "min(920px, 92vw)",
          height: "100vh",
          background: bg,
        },
      }}
    >
      {/* Content wrapper */}
      <Box sx={{ display: "grid", gridTemplateColumns: "320px 1fr", height: "100%" }}>
        {/* LEFT NAV */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: "24px 0 0 24px",
            background: cardBg,
            borderRight: border,
            p: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography sx={{ fontSize: 22, fontWeight: 900, color: textDark }}>
            Settings
          </Typography>

          <List sx={{ p: 0, mt: 1 }}>
            <NavItem
              label="Account"
              icon={<PersonOutlineIcon />}
              selected={active === "account"}
              onClick={() => setActive("account")}
            />
            <NavItem
              label="Subscription"
              icon={<StarOutlineIcon />}
              selected={active === "subscription"}
              onClick={() => setActive("subscription")}
            />
            <NavItem
              label="Customization"
              icon={<TuneIcon />}
              selected={active === "customization"}
              onClick={() => setActive("customization")}
            />
            <NavItem
              label="Advanced"
              icon={<SettingsSuggestIcon />}
              selected={active === "advanced"}
              onClick={() => setActive("advanced")}
            />
            <NavItem
              label="Help & Feedback"
              icon={<HelpOutlineIcon />}
              selected={active === "help"}
              onClick={() => setActive("help")}
            />
            <NavItem
              label="Share Schedule"
              icon={<ShareOutlinedIcon />}
              selected={active === "share"}
              onClick={() => setActive("share")}
            />
          </List>

          <Box sx={{ flex: 1 }} />

          <List sx={{ p: 0 }}>
            <NavItem
              label="Privacy"
              icon={<PrivacyTipOutlinedIcon />}
              selected={false}
              onClick={onOpenPrivacy}
              muted
            />
            <NavItem
              label="Log Out"
              icon={<LogoutOutlinedIcon />}
              selected={false}
              onClick={onLogout}
              muted
            />
          </List>
        </Paper>

        {/* RIGHT PANEL */}
        <Box sx={{ p: 4, position: "relative" }}>
          {/* Close (top-right) */}
          <IconButton
            onClick={onClose}
            aria-label="Close settings"
            sx={{
              position: "absolute",
              top: 18,
              right: 18,
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: "white",
              border,
              "&:hover": { background: "white" },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Panel content */}
          {active === "account" && (
            <AccountPanel
              userEmail={userEmail}
              syncProviderLabel={syncProviderLabel}
              syncStatusLabel={syncStatusLabel}
              onResync={onResync}
              onDeleteAccount={onDeleteAccount}
              onResetApp={onResetApp}
            />
          )}

          {active === "share" && (
            <SharePanel userId={userId} />
          )}

          {active === "customization" && <CustomizationPanel />}

          {active !== "account" && active !== "share" && active !== "customization" && (
            <PlaceholderPanel title={titleFor(active)} />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}

/* ----------------------------- Subcomponents ----------------------------- */

function NavItem({
  label,
  icon,
  selected,
  onClick,
  muted: mutedStyle = false,
}) {
  const { tokens } = useAppTheme();
  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        borderRadius: 999,
        py: 1,
        px: 1.5,
        mb: 0.75,
        background: selected ? tokens.primaryLight : "transparent",
        "&:hover": { background: selected ? tokens.primaryLight : "rgba(0,0,0,0.03)" },
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 36,
          color: selected ? "white" : mutedStyle ? "rgba(0,0,0,0.35)" : muted,
        }}
      >
        {icon}
      </ListItemIcon>
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          sx: {
            fontWeight: selected ? 800 : 600,
            color: selected ? "white" : mutedStyle ? "rgba(0,0,0,0.35)" : muted,
          },
        }}
      />
    </ListItemButton>
  );
}

function AccountPanel({
  userEmail,
  syncProviderLabel,
  syncStatusLabel,
  onResync,
  onDeleteAccount,
  onResetApp,
}) {
  const { tokens } = useAppTheme();
  return (
    <Box sx={{ maxWidth: 520 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 900, color: textDark, mb: 2 }}>
        Account
      </Typography>

      {/* Provider */}
      <Typography sx={{ fontSize: 13, fontWeight: 800, color: textDark, mb: 0.75 }}>
        {syncProviderLabel}
      </Typography>

      {/* SYNC row */}
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1 }}>
        <Typography sx={{ fontSize: 10, letterSpacing: 1.2, color: "rgba(0,0,0,0.35)" }}>
          SYNC
        </Typography>

        <Button
          variant="text"
          onClick={onResync}
          sx={{
            textTransform: "none",
            fontSize: 12,
            fontWeight: 800,
            color: tokens.primary,
            p: 0,
            minWidth: 0,
          }}
        >
          Resync
        </Button>
      </Box>

      <InfoCard
        icon={<CloudOutlinedIcon sx={{ color: tokens.primary }} />}
        text={syncStatusLabel}
      />

      {/* ACCOUNT */}
      <Typography sx={{ fontSize: 10, letterSpacing: 1.2, color: "rgba(0,0,0,0.35)", mt: 2 }}>
        ACCOUNT
      </Typography>

      <InfoCard
        icon={<AlternateEmailOutlinedIcon sx={{ color: tokens.primary }} />}
        text={userEmail}
      />

      <Divider sx={{ my: 3, borderColor: "rgba(0,0,0,0.06)" }} />

      {/* Danger Zone */}
      <Typography sx={{ fontSize: 14, fontWeight: 900, color: textDark, mb: 1.5 }}>
        Danger Zone
      </Typography>

      <Box sx={{ display: "flex", gap: 2 }}>
        <DangerButton
          label="Delete Account"
          icon={<DeleteOutlineIcon />}
          variant="delete"
          onClick={onDeleteAccount}
        />
        <DangerButton
          label="Reset App"
          icon={<RestartAltIcon />}
          variant="reset"
          onClick={onResetApp}
        />
      </Box>
    </Box>
  );
}

function InfoCard({ icon, text }) {
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 1,
        borderRadius: 3,
        border,
        background: "white",
        px: 2,
        py: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>{icon}</Box>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: textDark }}>
        {text}
      </Typography>
    </Paper>
  );
}

function DangerButton({
  label,
  icon,
  variant,
  onClick,
}) {
  const isDelete = variant === "delete";
  const stroke = isDelete ? "rgb(232, 48, 48)" : "rgb(245, 158, 11)";
  const fill = isDelete ? "rgba(232,48,48,0.08)" : "rgba(245,158,11,0.10)";

  return (
    <Button
      onClick={onClick}
      startIcon={icon}
      variant="outlined"
      sx={{
        textTransform: "none",
        fontWeight: 900,
        borderRadius: 999,
        px: 3,
        py: 1.25,
        borderColor: stroke,
        color: stroke,
        background: fill,
        "&:hover": { background: fill, borderColor: stroke },
      }}
    >
      {label}
    </Button>
  );
}

function SharePanel({ userId }) {
  const { tokens: themeTokens } = useAppTheme();
  const [tokens, setTokens] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [generating, setGenerating] = React.useState(false);
  const [copied, setCopied] = React.useState(false);
  const [copiedId, setCopiedId] = React.useState(null);

  const shareBase = `${window.location.origin}/shared/`;

  const reload = React.useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const list = await listShareTokens(userId);
      setTokens(list);
    } catch (err) {
      console.error('Failed to list share tokens:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  React.useEffect(() => { reload(); }, [reload]);

  const handleGenerate = async () => {
    if (!userId) return;
    setGenerating(true);
    try {
      const token = await createShareToken(userId, 'Shared schedule');
      const url = `${shareBase}${token}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      await reload();
    } catch (err) {
      console.error('Failed to generate share token:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async (token) => {
    const url = `${shareBase}${token}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(token);
    setTimeout(() => setCopiedId(null), 2500);
  };

  const handleRevoke = async (docId) => {
    try {
      await revokeShareToken(docId);
      await reload();
    } catch (err) {
      console.error('Failed to revoke share token:', err);
    }
  };

  return (
    <Box sx={{ maxWidth: 520 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 900, color: textDark, mb: 0.5 }}>
        Share Schedule
      </Typography>
      <Typography sx={{ fontSize: 13, color: muted, mb: 2.5 }}>
        Generate a read-only link so others can view your daily schedule without an account.
      </Typography>

      <Button
        variant="contained"
        startIcon={generating ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <LinkIcon />}
        onClick={handleGenerate}
        disabled={generating || !userId}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 999,
          background: themeTokens.primary,
          '&:hover': { background: themeTokens.primaryHover },
          mb: 3,
        }}
      >
        {copied ? 'Link copied!' : 'Generate share link'}
      </Button>

      {loading ? (
        <CircularProgress size={20} sx={{ color: themeTokens.primary }} />
      ) : tokens.length > 0 ? (
        <Box>
          <Typography sx={{ fontSize: 10, letterSpacing: 1.2, color: 'rgba(0,0,0,0.35)', mb: 1 }}>
            ACTIVE LINKS
          </Typography>
          {tokens.map(t => (
            <Paper
              key={t.id}
              elevation={0}
              sx={{
                mb: 1.5,
                borderRadius: 3,
                border,
                background: 'white',
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <LinkIcon sx={{ color: themeTokens.primary, flexShrink: 0 }} fontSize="small" />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: textDark }}>
                  {t.label}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 11,
                    color: muted,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {shareBase}{t.token}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleCopy(t.token)}
                title="Copy link"
                sx={{
                  flexShrink: 0,
                  color: copiedId === t.token ? 'rgb(34,197,94)' : muted,
                  transition: 'color 0.2s',
                }}
              >
                {copiedId === t.token ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
              </IconButton>
              <Button
                size="small"
                startIcon={<LinkOffIcon />}
                onClick={() => handleRevoke(t.id)}
                sx={{
                  textTransform: 'none',
                  fontSize: 12,
                  fontWeight: 700,
                  color: 'rgb(232,48,48)',
                  flexShrink: 0,
                }}
              >
                Revoke
              </Button>
            </Paper>
          ))}
        </Box>
      ) : (
        <Typography sx={{ fontSize: 13, color: muted }}>No active share links.</Typography>
      )}
    </Box>
  );
}

function CustomizationPanel() {
  const { tokens, themeId, customColor, selectPreset, selectCustom, PRESETS: presets } = useAppTheme();
  return (
    <Box sx={{ maxWidth: 520 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 900, color: textDark, mb: 3 }}>
        Customization
      </Typography>
      <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.2, color: "rgba(0,0,0,0.35)", mb: 1.5 }}>
        ACCENT COLOR
      </Typography>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 3 }}>
        {presets.map(preset => (
          <Box
            key={preset.id}
            onClick={() => selectPreset(preset.id)}
            title={preset.name}
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              background: preset.primary,
              cursor: "pointer",
              outline: themeId === preset.id ? `3px solid ${tokens.primary}` : "none",
              outlineOffset: 2,
            }}
          />
        ))}
        {/* Custom color swatch */}
        <Box
          sx={{
            position: "relative",
            width: 36,
            height: 36,
            borderRadius: "50%",
            overflow: "hidden",
            cursor: "pointer",
            background: customColor ?? "conic-gradient(red,yellow,lime,cyan,blue,magenta,red)",
            outline: themeId === "custom" ? `3px solid ${tokens.primary}` : "none",
            outlineOffset: 2,
          }}
        >
          <input
            type="color"
            value={customColor ?? "#f49f99"}
            onChange={e => selectCustom(e.target.value)}
            style={{
              position: "absolute",
              inset: 0,
              opacity: 0,
              width: "100%",
              height: "100%",
              cursor: "pointer",
            }}
          />
        </Box>
      </Box>
      <Typography sx={{ fontSize: 13, color: muted }}>
        Changes apply instantly across the entire app.
      </Typography>
    </Box>
  );
}

function PlaceholderPanel({ title }) {
  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 900, color: textDark, mb: 1 }}>
        {title}
      </Typography>
      <Typography sx={{ color: muted }}>
        Wire this section to your app's settings pages. The left nav state handling is already implemented.
      </Typography>
    </Box>
  );
}

function titleFor(s) {
  switch (s) {
    case "subscription":
      return "Subscription";
    case "customization":
      return "Customization";
    case "advanced":
      return "Advanced";
    case "help":
      return "Help & Feedback";
    case "share":
      return "Share Schedule";
    default:
      return "Account";
  }
}

export default SettingsIconWithDrawer;
